import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { formatContestSectionBlockNumber, sectionBlocksHaveContent } from '@utils/contestCatalog';
import type { ContestSectionBlock } from '../../types/contestCatalog';

function renderInline(text: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    if (match[1]) nodes.push(<strong key={`b-${key++}`}>{match[1]}</strong>);
    else nodes.push(<em key={`i-${key++}`}>{match[2]}</em>);
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export const ContestRichText: React.FC<{ text?: string }> = ({ text }) => {
  if (!String(text || '').trim()) return null;
  const rows: React.ReactNode[] = [];
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  let listBuf: string[] = [];

  const flushList = () => {
    if (!listBuf.length) return;
    const items = [...listBuf];
    listBuf = [];
    rows.push(
      <Box component="ul" key={`ul-${rows.length}`} sx={{ m: 0, pl: 2.25, mb: 1 }}>
        {items.map((item, index) => (
          <Typography
            key={index}
            component="li"
            variant="body2"
            sx={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.55 }}
          >
            {renderInline(item)}
          </Typography>
        ))}
      </Box>,
    );
  };

  lines.forEach((line, index) => {
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      listBuf.push(bullet[1]);
      return;
    }
    flushList();
    if (!line.trim()) return;
    const heading = line.match(/^##\s+(.*)$/);
    if (heading) {
      rows.push(
        <Typography
          key={`h-${index}`}
          variant="subtitle2"
          sx={{ fontWeight: 800, color: brandColors.navy, mt: 0.5, mb: 0.5 }}
        >
          {renderInline(heading[1])}
        </Typography>,
      );
      return;
    }
    rows.push(
      <Typography
        key={`p-${index}`}
        variant="body2"
        sx={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.55, mb: 0.75 }}
      >
        {renderInline(line)}
      </Typography>,
    );
  });
  flushList();
  return <Box>{rows}</Box>;
};

function safeImageUrl(url: string): string | null {
  const value = String(url || '').trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value;
  return null;
}

export const ContestSectionBlocksView: React.FC<{
  blocks?: ContestSectionBlock[];
  sectionIndex?: number;
}> = ({ blocks, sectionIndex = 0 }) => {
  const visible = (blocks || []).filter(
    (block) =>
      String(block.title || '').trim() ||
      String(block.body || '').trim() ||
      (block.images || []).some((image) => safeImageUrl(image.url)),
  );
  if (!sectionBlocksHaveContent(visible)) return null;

  return (
    <Stack spacing={1.5}>
      {visible.map((block, index) => {
        const numberLabel = formatContestSectionBlockNumber(sectionIndex, index);
        const images = (block.images || [])
          .map((image) => ({ ...image, url: safeImageUrl(image.url) || '' }))
          .filter((image) => image.url);
        return (
          <Box key={block.id || numberLabel}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={numberLabel}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: brandColors.blue,
                  color: '#fff',
                }}
              />
              {block.title ? (
                <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.88rem' }}>
                  {block.title}
                </Typography>
              ) : null}
            </Stack>
            <ContestRichText text={block.body} />
            {images.length > 0 && (
              <Stack spacing={1} sx={{ mt: 0.75 }}>
                {images.map((image, imageIndex) => (
                  <Box key={`${block.id}-img-${imageIndex}`}>
                    <Box
                      component="img"
                      src={image.url}
                      alt={image.caption || block.title || numberLabel}
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: 280,
                        borderRadius: 1.5,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                    {image.caption ? (
                      <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mt: 0.35 }}>
                        {image.caption}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
